import React, { useEffect, useState } from "react";
import axiosInstance from "../../interceptors/token";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../constants";
import { WS_BASE_URL } from "../../constants";
import "../../styles/Projects.css";
import Header from "../Header";
import { Trans } from "@lingui/react/macro";
import { useLanguage } from "../LanguageProvider";
import { i18n } from "@lingui/core";
import ProjectItem from "./ProjectItem";
import PopupManager from "./PopupManager";
import { fetchProjects } from "./services/projectService";

const Projects = () => {
  const { language } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [translatedDescriptions, setTranslatedDescriptions] = useState({});
  const [timingData, setTimingData] = useState({});
  const [selectedDate, setSelectedDate] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });
  const [popupDetails, setPopupDetails] = useState({
    action: "",
    project: null,
  });
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState({});
  const [previousWorkDayFault, setPreviousWorkDayFault] = useState(null);
  const [isCreatingProject, setIsCreatingProject] = useState(false);

  const [refresh, setRefresh] = useState(false);

  const validateFields = () => {
    const errors = {};
    const errorMessage = i18n._("This field must be filled in!");
    if (!popupDetails.data.customer_name.trim())
      errors.customer_name = errorMessage;
    if (!popupDetails.data.description.trim())
      errors.description = errorMessage;
    if (!popupDetails.data.street.trim()) errors.street = errorMessage;
    if (!popupDetails.data.house_number.trim())
      errors.house_number = errorMessage;
    if (!popupDetails.data.postal_number.trim())
      errors.postal_number = errorMessage;
    if (!popupDetails.data.town.trim()) errors.town = errorMessage;

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      navigate("/");
      return;
    }

    fetchProjects(
      selectedDate,
      language,
      setPreviousWorkDayFault,
      setProjects,
      setProjectsLoading,
      setTimingData,
      setTranslatedDescriptions
    );
  }, [selectedDate, navigate, language, refresh]);

  useEffect(() => {
    const ws = new WebSocket(WS_BASE_URL + "mappings/");

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "mapping_updated") {
        setRefresh((prev) => !prev);
      }
    };

    ws.onerror = (error) => console.error("WebSocket Error:", error);
    ws.onclose = () => console.log("WebSocket connection closed");

    return () => ws.close();
  }, []);

  const handleDateChange = (event) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);
  };

  const handleNavigateToProject = (projectId) => {
    navigate(`/projects/${projectId}`, {
      state: { selectedDate: selectedDate },
    });
  };

  const handleChangeOrder = async (moveUp, index) => {
    setProjects((prevProjects) => {
      const updatedProjects = [...prevProjects];
      const newIndex = moveUp ? index - 1 : index + 1;

      [updatedProjects[index], updatedProjects[newIndex]] = [
        updatedProjects[newIndex],
        updatedProjects[index],
      ];

      axiosInstance
        .put(`${API_BASE_URL}projects/order/`, {
          date: selectedDate,
          project_order: updatedProjects.map((project) => project.project_id),
        })
        .catch((error) =>
          console.error("Error updating project order:", error)
        );

      return updatedProjects;
    });
  };

  const handleCreateProject = async () => {
    if (!validateFields()) {
      return;
    }

    const confirmSubmit = window.confirm(
      i18n._(
        "Are you sure you want to create this new project? This is only ever needed in exceptional cases!"
      )
    );
    if (!confirmSubmit) return;

    setIsCreatingProject(true);
    try {
      const projectResponse = await axiosInstance.post(
        API_BASE_URL + "projects/",
        {
          user_created: true,
          selected_date: selectedDate,
          ...popupDetails.data,
        }
      );
      const newProject = projectResponse.data;

      await axiosInstance.post(
        API_BASE_URL + `projects/${newProject.project_id}/priority/`,
        {
          date: selectedDate,
          user: "dummy",
          priority: 1000,
        }
      );

      await axiosInstance.patch(API_BASE_URL + "mappings/", {
        date: selectedDate,
        project_id: newProject.project_id,
      });

      setProjects((prevProjects) => [
        ...prevProjects,
        {
          ...newProject,
          priority: 1000,
        },
      ]);
    } catch (error) {
      console.error("Error creating project:", error);
    }

    closePopup();
    setIsCreatingProject(false);
  };

  const handleTimingAction = async (action, project) => {
    const currentTime = new Date().toTimeString().split(" ")[0].substring(0, 5);

    const timingData = {
      date: selectedDate,
      [action]: currentTime,
    };

    closePopup();

    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}projects/${project.project_id}/werk/timing/`,
        timingData
      );
      setTimingData((prev) => ({
        ...prev,
        [project.project_id]: { ...prev[project.project_id], ...response.data },
      }));
    } catch (error) {
      console.error(`Error logging ${action} time:`, error);
    }
  };

  const showPopup = (action, data) => {
    setPopupDetails({ action, data });
  };

  const closePopup = () => {
    setPopupDetails({ action: "", data: null });
    setValidationErrors({});
  };

  return (
    <div>
      <Header />
      <div className="container">
        <div className="headingContainer">
          <h1 className="heading">
            <Trans>Projects</Trans>
          </h1>
          <button
            className="refreshButton"
            onClick={() => setRefresh(!refresh)}
          >
            🔄
          </button>
        </div>
        <div className="dateSelector">
          <label htmlFor="date">
            <Trans>Select Date:</Trans>
          </label>
          <input
            type="date"
            id="date"
            value={selectedDate}
            onChange={handleDateChange}
            className="dateInput"
          />
        </div>

        {projectsLoading ? (
          <p className="noProjectsMessage">
            <Trans>Loading projects...</Trans>
          </p>
        ) : previousWorkDayFault ? (
          <div className="errorBox">
            <p>
              <Trans>Please complete your delivery note entries on </Trans>{" "}
              {previousWorkDayFault}
              <br />
              <Trans>before accessing projects on </Trans> {selectedDate}
            </p>
          </div>
        ) : (
          <div>
            <div className="projectList">
              {projects.length > 0 ? (
                projects.map((project, index) => (
                  <ProjectItem
                    key={project.project_id}
                    project={project}
                    index={index}
                    isLastItem={index === projects.length - 1}
                    translatedDescription={
                      translatedDescriptions[project.project_id]
                    }
                    timing={timingData[project.project_id]}
                    showPopup={showPopup}
                    handleNavigateToProject={handleNavigateToProject}
                    handleChangeOrder={handleChangeOrder}
                  />
                ))
              ) : (
                <p className="noProjectsMessage">
                  <Trans>No projects found for the selected date.</Trans>
                </p>
              )}
            </div>
            <div
              className="projectRow createNewProject"
              onClick={() =>
                showPopup("create_project", {
                  customer_name: "",
                  description: "",
                  street: "",
                  house_number: "",
                  postal_number: "",
                  town: "",
                })
              }
            >
              <div className="createNewProjectContent">
                <h3 className="createNewProjectTitle">
                  + <Trans>Create New Project</Trans>
                </h3>
              </div>
            </div>
          </div>
        )}
        <PopupManager
          popupDetails={popupDetails}
          setPopupDetails={setPopupDetails}
          validationErrors={validationErrors}
          handleCreateProject={handleCreateProject}
          handleTimingAction={handleTimingAction}
          closePopup={closePopup}
          isCreatingProject={isCreatingProject}
          setIsCreatingProject={setIsCreatingProject}
        />
      </div>
    </div>
  );
};

export default Projects;
